// Spec 078 US5 — a rota ambiente (TV): cena em tela cheia + barra de contexto, sem sidebar nem
// topbar. Duas entradas com a MESMA casca: `/lines/:id/ambient` (sessão; dentro de
// ProtectedLayout, fora de AppLayout — o realtime é montado aqui) e `/display/:token` (pública,
// sem login; polling de 4 s pela rota de display). Sem WebGL, cai no painel textual "far".
import * as React from "react";
import { useParams } from "react-router-dom";
import { Notify } from "@/components/ui/notify";
import { AmbientBar } from "@/features/lines/scene/ambient/AmbientBar";
import { AmbientInactive } from "@/features/lines/scene/ambient/AmbientInactive";
import { useSceneSource } from "@/features/lines/scene/hooks/useSceneSource";
import { useStaleness } from "@/features/lines/scene/hooks/useStaleness";
import { useWebGLSupport } from "@/features/lines/scene/hooks/useWebGLSupport";
import { SceneTextPanel } from "@/features/lines/scene/LineScene/SceneTextPanel";
import { SceneSkeleton } from "@/features/lines/scene/SceneSkeleton";
import { isApiClientError } from "@/lib/api-client";
import { useRealtimeConnection } from "@/lib/use-realtime-connection";
import { useRealtimeInvalidate } from "@/lib/use-realtime-invalidate";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";

const LineScene = React.lazy(() =>
  import("@/features/lines/scene/LineSceneEntry").then((m) => ({ default: m.LineScene }))
);

interface AmbientShellProps {
  lineId: string;
  displayToken?: string;
}

function AmbientShell({ lineId, displayToken }: AmbientShellProps): React.ReactElement {
  const { query, linkInactive } = useSceneSource(lineId, displayToken);
  const staleness = useStaleness(query.dataUpdatedAt);
  const webgl = useWebGLSupport();
  const scene = query.data;

  React.useEffect(() => {
    if (scene) document.title = `${scene.meta.lineName} · Opus DT`;
  }, [scene]);

  if (linkInactive) return <AmbientInactive reason="link" />;
  if (query.isError && isApiClientError(query.error) && query.error.status === 404) {
    return <AmbientInactive reason="line" />;
  }

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-bg-inverse text-text-inverse"
      data-testid="ambient-page"
    >
      <AmbientBar scene={scene} staleness={staleness} />
      <main className="min-h-0 flex-1 overflow-hidden">
        {query.isError ? (
          <div className="p-8">
            <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : webgl ? (
          <React.Suspense fallback={<SceneSkeleton />}>
            <LineScene lineId={lineId} displayToken={displayToken} mode="ambient" height="100%" />
          </React.Suspense>
        ) : scene ? (
          <SceneTextPanel scene={scene} size="far" className="p-8" />
        ) : (
          <SceneSkeleton />
        )}
      </main>
    </div>
  );
}

/** Liga o WS desta linha — o AppLayout (que normalmente faz isso) não está montado aqui. */
function RealtimeBridge({ lineId }: { lineId: string }): null {
  useRealtimeConnection();
  useRealtimeRooms([lineId]);
  useRealtimeInvalidate();
  return null;
}

/** `/lines/:id/ambient` — sessão autenticada. */
export function LineAmbientPage(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  return (
    <>
      <RealtimeBridge lineId={id} />
      <AmbientShell lineId={id} />
    </>
  );
}

/** `/display/:token` — pública; a organização vem do token, nunca da URL. */
export function DisplayPage(): React.ReactElement {
  const { token = "" } = useParams<{ token: string }>();
  return <AmbientShell lineId="" displayToken={token} />;
}
